<div class="channel-runs-container">
  <!-- Header -->
  <div class="section-header">
    <div class="header-content">
      <h2>Channel Runs</h2>
      <p class="section-description">View message processing run history</p>
    </div>
    <button class="btn btn-secondary" (click)="refresh()" [disabled]="isLoading">
      <i class="fas" [ngClass]="isLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
      Refresh
    </button>
  </div>

  <!-- Messages -->
  <div class="message error-message" *ngIf="errorMessage">
    <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
  </div>

  <!-- Loading State -->
  <div class="loading-container" *ngIf="isLoading">
    <i class="fas fa-spinner fa-spin"></i> Loading runs...
  </div>

  <!-- Filters -->
  <div class="filters-row" *ngIf="!isLoading">
    <div class="search-box">
      <i class="fas fa-search"></i>
      <input type="text" [(ngModel)]="searchQuery" placeholder="Search by channel name..." />
    </div>
    <div class="filter-group" *ngIf="!channelId">
      <label>Channel:</label>
      <select [(ngModel)]="channelFilter">
        <option value="all">All Channels</option>
        <option *ngFor="let channel of channels" [value]="channel.ID">{{ channel.Name }}</option>
      </select>
    </div>
    <div class="filter-group">
      <label>Status:</label>
      <select [(ngModel)]="statusFilter">
        <option value="all">All</option>
        <option value="Completed">Completed</option>
        <option value="Running">Running</option>
        <option value="Failed">Failed</option>
        <option value="Pending">Pending</option>
      </select>
    </div>
  </div>

  <!-- Runs List -->
  <div class="runs-container" *ngIf="!isLoading">
    <div class="runs-table card">
      <table>
        <thead>
          <tr>
            <th>Status</th>
            <th *ngIf="!channelId">Channel</th>
            <th>Started</th>
            <th>Duration</th>
            <th>Messages</th>
            <th>Processed</th>
            <th>Failed</th>
            <th>Success Rate</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let rd of filteredRuns"
              [class.selected]="selectedRun === rd"
              [class.has-error]="rd.run.ErrorMessage"
              (click)="selectRun(rd)">
            <td class="status-cell">
              <span class="status-badge" [ngClass]="getStatusClass(rd.run.Status)">
                <i class="fas" [ngClass]="getStatusIcon(rd.run.Status)"></i>
                {{ rd.run.Status }}
              </span>
            </td>
            <td *ngIf="!channelId" class="channel-cell">
              <span *ngIf="rd.channel">{{ rd.channel.Name }}</span>
              <span *ngIf="!rd.channel" class="unknown">Unknown</span>
            </td>
            <td class="date-cell">{{ formatDate(rd.run.StartedAt) }}</td>
            <td class="duration-cell">{{ formatDuration(rd.run.StartedAt, rd.run.CompletedAt) }}</td>
            <td class="number-cell">{{ rd.run.MessageCount || 0 }}</td>
            <td class="number-cell">{{ rd.run.ProcessedCount || 0 }}</td>
            <td class="number-cell" [class.has-failures]="rd.run.FailedCount && rd.run.FailedCount > 0">
              {{ rd.run.FailedCount || 0 }}
            </td>
            <td class="rate-cell">
              <div class="rate-bar">
                <div class="rate-fill" [style.width.%]="getSuccessRate(rd)"></div>
              </div>
              <span class="rate-value">{{ getSuccessRate(rd) }}%</span>
            </td>
            <td class="action-cell">
              <button
                class="btn btn-view-messages"
                (click)="openDrillDown(rd, $event)"
                title="View messages in this run"
              >
                <i class="fas fa-envelope"></i>
                <span>Messages</span>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Empty State -->
      <div class="empty-state" *ngIf="filteredRuns.length === 0">
        <i class="fas fa-history"></i>
        <p>No runs found</p>
      </div>
    </div>

    <!-- Run Details Panel -->
    <div class="run-details card" *ngIf="selectedRun">
      <div class="details-header">
        <h3>Run Details</h3>
        <button class="btn btn-icon" (click)="selectedRun = null">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="details-content">
        <div class="detail-row">
          <span class="detail-label">Channel</span>
          <span class="detail-value">{{ selectedRun.channel?.Name || 'Unknown' }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Status</span>
          <span class="status-badge" [ngClass]="getStatusClass(selectedRun.run.Status)">
            <i class="fas" [ngClass]="getStatusIcon(selectedRun.run.Status)"></i>
            {{ selectedRun.run.Status }}
          </span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Started</span>
          <span class="detail-value">{{ formatDate(selectedRun.run.StartedAt) }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Completed</span>
          <span class="detail-value">{{ formatDate(selectedRun.run.CompletedAt) }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Duration</span>
          <span class="detail-value">{{ formatDuration(selectedRun.run.StartedAt, selectedRun.run.CompletedAt) }}</span>
        </div>
        <div class="stats-grid">
          <div class="stat-item">
            <span class="stat-value">{{ selectedRun.run.MessageCount || 0 }}</span>
            <span class="stat-label">Total Messages</span>
          </div>
          <div class="stat-item">
            <span class="stat-value">{{ selectedRun.run.ProcessedCount || 0 }}</span>
            <span class="stat-label">Processed</span>
          </div>
          <div class="stat-item" [class.has-failures]="selectedRun.run.FailedCount && selectedRun.run.FailedCount > 0">
            <span class="stat-value">{{ selectedRun.run.FailedCount || 0 }}</span>
            <span class="stat-label">Failed</span>
          </div>
        </div>
        <div class="error-section" *ngIf="selectedRun.run.ErrorMessage">
          <h4><i class="fas fa-exclamation-triangle"></i> Error Message</h4>
          <pre class="error-content">{{ selectedRun.run.ErrorMessage }}</pre>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Drill-Down Modal -->
<div class="drill-down-overlay" *ngIf="showDrillDown" (click)="closeDrillDown()">
  <div class="drill-down-panel" (click)="$event.stopPropagation()">
    <!-- Drill-Down Header -->
    <div class="drill-down-header" *ngIf="drillDownRun">
      <div class="drill-down-title">
        <div class="drill-down-icon" [style.background]="getStatusColor(drillDownRun.run.Status)">
          <i class="fas fa-sync"></i>
        </div>
        <div class="drill-down-info">
          <h2>Channel Run Messages</h2>
          <span class="drill-down-subtitle">
            {{ drillDownRun.channel?.Name || 'Unknown Channel' }} &bull;
            {{ formatDate(drillDownRun.run.StartedAt) }}
          </span>
        </div>
      </div>
      <button class="close-btn" (click)="closeDrillDown()">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- Drill-Down Summary -->
    <div class="drill-down-summary" *ngIf="drillDownRun">
      <div class="summary-stat">
        <span class="stat-value">{{ drillDownRun.run.MessageCount || 0 }}</span>
        <span class="stat-label">Total</span>
      </div>
      <div class="summary-stat">
        <span class="stat-value success">{{ drillDownRun.run.ProcessedCount || 0 }}</span>
        <span class="stat-label">Processed</span>
      </div>
      <div class="summary-stat">
        <span class="stat-value danger">{{ drillDownRun.run.FailedCount || 0 }}</span>
        <span class="stat-label">Failed</span>
      </div>
      <div class="summary-stat">
        <span class="stat-value">{{ getSuccessRate(drillDownRun) }}%</span>
        <span class="stat-label">Success Rate</span>
      </div>
    </div>

    <!-- Loading State -->
    <div class="drill-down-loading" *ngIf="isDrillDownLoading">
      <i class="fas fa-spinner fa-spin"></i>
      <span>Loading messages...</span>
    </div>

    <!-- Drill-Down Content -->
    <div class="drill-down-content" *ngIf="!isDrillDownLoading">
      <div class="drill-down-empty" *ngIf="drillDownMessages.length === 0">
        <i class="fas fa-inbox"></i>
        <p>No messages found for this run</p>
      </div>

      <div class="drill-down-list" *ngIf="drillDownMessages.length > 0">
        <div
          class="message-row clickable"
          *ngFor="let msg of drillDownMessages"
          (click)="navigateToMessage(msg)"
          title="Click to view in Messages"
        >
          <div class="message-icon" [style.background]="getStatusColor(msg.GenerationStatus)">
            <i class="fas fa-envelope"></i>
          </div>
          <div class="message-details">
            <div class="message-main">
              <span class="message-sender">{{ msg.Sender || 'Unknown Sender' }}</span>
              <span class="message-subject">{{ truncateText(msg.Subject || 'No Subject', 50) }}</span>
            </div>
            <div class="message-meta">
              <span class="badge" [style.background]="getStatusColor(msg.GenerationStatus)">
                {{ msg.GenerationStatus }}
              </span>
              <span class="badge" [style.background]="getStatusColor(msg.ApprovalStatus)">
                {{ msg.ApprovalStatus }}
              </span>
              <span class="channel-badge">
                <i class="fas fa-broadcast-tower"></i>
                {{ getChannelName(msg.ChannelID) }}
              </span>
              <span class="time">{{ getTimeSince(msg.ReceivedAt) }}</span>
            </div>
          </div>
          <div class="message-nav-hint">
            <i class="fas fa-external-link-alt"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- Drill-Down Footer -->
    <div class="drill-down-footer" *ngIf="!isDrillDownLoading && drillDownMessages.length > 0">
      <span class="showing-count">Showing {{ drillDownMessages.length }} message(s)</span>
      <button class="close-action-btn" (click)="closeDrillDown()">
        <i class="fas fa-times"></i> Close
      </button>
    </div>
  </div>
</div>
